import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import cloneDeep from 'lodash.clonedeep';
import { firstValueFrom, throwError } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

// Ngrx
import { setCustomPDFReportStore } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/custom-pdf-report/custom-pdf-report.action';
import { setIncludeBenchmarkDefault } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/include-benchmark-default/include-benchmark-default.action';
import { setTabs } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/tabs/tabs.action';
import { getNumRequiredSecurities } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/custom-pdf-report-builder.selector';

// Interfaces and constants
import { CUSTOM_PDF_REPORT_BUILDER_APP_STATE_SLICE_KEY } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/constants';
import { CustomPDFReportOrientation } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-orientation.enum';
import { CustomPDFReportType } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-type.enum';
import { CustomPDFReportModuleMenu } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/types/custom-pdf-report-module-menu.type';
import {
    UpdateReportBuilderModuleParams,
    UpdateReportBuilderParams,
    UpdateReportBuilderResult,
} from 'AgentApp/ts/interfaces/update-report-builder-tool.interfaces';
import { CustomPDFReportModule } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-module.interface';
import { CustomPDFReportTab } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-tab.interface';
import { CustomPDFReportBuilderStore } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/interfaces/custom-pdf-report-builder-store.interface';
import { CustomPDFReportStore } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/interfaces/custom-pdf-report-store.interface';
import { CustomPDFReportDataSource } from 'CustomPDFReportsApp/ts/interfaces/custom-pdf-reports.interfaces';

// Services
import { CustomPDFReportService } from 'CustomPDFReportsApp/ts/services/custom-pdf-report.service';

// Utils
import { addModuleToReport } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/utils/custom-pdf-report-builder-utils';
import { getModuleIdentityKey } from 'CustomPDFReportsApp/ts/utils/custom-pdf-report-utils';

const DATA_SOURCES: CustomPDFReportDataSource[] = ['legacy', 'default'];

const EDITABLE_FIELDS: (keyof UpdateReportBuilderParams)[] = [
    'report_name',
    'report_description',
    'report_type',
    'report_orientation',
    'data_source',
    'include_benchmark_default',
    'modules',
];

const isReportLoaded = (report: CustomPDFReportStore | undefined): boolean =>
    !!report?.report_id && Object.keys(report.prev_save_params ?? {}).length > 0;

@Injectable({ providedIn: 'root' })
export class UpdateReportBuilderAppStateClientTool {
    private readonly _reportService = inject(CustomPDFReportService);

    static loadTimeoutMs = 15000;

    private readonly _store = inject(Store, { optional: true });

    async execute(input: UpdateReportBuilderParams): Promise<UpdateReportBuilderResult> {
        let builderState = await this._readReportBuilderState();
        if (!builderState?.report) {
            throw new Error('The Report Builder is not open on this page.');
        }
        const params = input ?? {};
        const errors = this._validate(params);
        if (errors.length) {
            throw new Error(errors.join(' '));
        }
        const applied: string[] = [];
        const warnings: string[] = [];

        if (params.report_id != null) {
            builderState = await this._openReport(params.report_id, builderState);
            applied.push('report_id');
        }
        const { report } = builderState;
        const changes = this._settingsChanges(params, report);
        applied.push(...Object.keys(changes));

        if (params.modules !== undefined) {
            const target = { ...report, ...changes };
            const menu = await firstValueFrom(
                this._reportService.getPDFReportModuleCategoryMenu(
                    target.report_orientation!,
                    target.report_type!,
                    target.data_source,
                    getNumRequiredSecurities({ ...builderState, report: target })
                )
            );
            changes.modules = this._buildModules(params.modules, report.modules, menu, target, warnings);
            applied.push('modules');
        }

        if (!applied.length) {
            throw new Error('Every provided value matches the current Report Builder state; nothing to change.');
        }

        const updatedReport: CustomPDFReportStore = { ...report, ...changes };
        if (Object.keys(changes).length) {
            if ('include_benchmark_default' in changes) {
                this._store!.dispatch(
                    setIncludeBenchmarkDefault({ include_benchmark_default: changes.include_benchmark_default! })
                );
            }
            this._store!.dispatch(setCustomPDFReportStore(updatedReport));
        }

        return {
            applied,
            warnings,
            report: {
                report_id: updatedReport.report_id,
                name: updatedReport.name,
                description: updatedReport.description,
                report_type: updatedReport.report_type,
                report_orientation: updatedReport.report_orientation,
                data_source: updatedReport.data_source,
                include_benchmark_default: updatedReport.include_benchmark_default,
                num_pages: updatedReport.modules.length,
                num_modules: updatedReport.modules.reduce((count, page) => count + page.length, 0),
            },
        };
    }

    private async _readReportBuilderState(): Promise<CustomPDFReportBuilderStore | null> {
        if (!this._store) {
            return null;
        }
        const snapshot: any = await firstValueFrom(this._store.select((state: any) => state).pipe(take(1)));
        return snapshot?.reducers?.[CUSTOM_PDF_REPORT_BUILDER_APP_STATE_SLICE_KEY] ?? null;
    }

    private async _openReport(
        reportId: number,
        builderState: CustomPDFReportBuilderStore
    ): Promise<CustomPDFReportBuilderStore> {
        const { report, tabs } = builderState;
        if (report.report_id !== reportId) {
            const openTabs = tabs ?? [];
            const existingIndex = openTabs.findIndex((tab) => tab.customPDFReportStore?.report_id === reportId);
            const newTabs: CustomPDFReportTab[] = openTabs.map((tab, index) =>
                tab.isActive
                    ? { ...tab, isActive: false, customPDFReportStore: report }
                    : { ...tab, isActive: index === existingIndex }
            );
            if (existingIndex === -1) {
                newTabs.push({
                    isActive: true,
                    isEdited: false,
                    customPDFReportStore: { report_id: reportId } as unknown as CustomPDFReportStore,
                });
            }
            this._store!.dispatch(setTabs({ tabs: newTabs }));
        }

        return firstValueFrom(
            this._store!.select((state: any) => state?.reducers?.[CUSTOM_PDF_REPORT_BUILDER_APP_STATE_SLICE_KEY]).pipe(
                filter(
                    (state: CustomPDFReportBuilderStore) =>
                        state?.report?.report_id === reportId && isReportLoaded(state.report)
                ),
                timeout({
                    each: UpdateReportBuilderAppStateClientTool.loadTimeoutMs,
                    with: () =>
                        throwError(
                            () =>
                                new Error(
                                    `Report #${reportId} did not load in the Report Builder. Check that it exists ` +
                                        'and that the user can edit it.'
                                )
                        ),
                })
            )
        );
    }

    private _validate(params: UpdateReportBuilderParams): string[] {
        const errors: string[] = [];
        const provided = EDITABLE_FIELDS.filter((field) => params[field] !== undefined);
        if (!provided.length && params.report_id == null) {
            errors.push(`Provide a report_id to open or at least one field to change: ${EDITABLE_FIELDS.join(', ')}.`);
        }
        if (params.report_id != null && !(Number.isInteger(params.report_id) && params.report_id > 0)) {
            errors.push('report_id must be the id of a saved report.');
        }
        if (params.report_name !== undefined && !String(params.report_name).trim()) {
            errors.push('report_name cannot be blank.');
        }
        const enums: [keyof UpdateReportBuilderParams, string[]][] = [
            ['report_type', Object.values(CustomPDFReportType)],
            ['report_orientation', Object.values(CustomPDFReportOrientation)],
            ['data_source', DATA_SOURCES],
        ];
        enums.forEach(([field, allowed]) => {
            const value = params[field];
            if (value !== undefined && !allowed.includes(value as string)) {
                errors.push(`${field} must be one of: ${allowed.join(', ')}.`);
            }
        });
        if (params.modules !== undefined) {
            const pages = params.modules;
            if (!Array.isArray(pages) || !pages.every(Array.isArray)) {
                errors.push('modules must be a list of pages, each a list of modules.');
            } else if (!pages.flat().length) {
                errors.push('modules must contain at least one page with at least one module.');
            } else if (pages.flat().some((module) => !module || typeof module.module_type !== 'string')) {
                errors.push('Every module needs a module_type.');
            }
        }
        return errors;
    }

    private _settingsChanges(
        params: UpdateReportBuilderParams,
        report: CustomPDFReportStore
    ): Partial<CustomPDFReportStore> {
        const changes: Partial<CustomPDFReportStore> = {};
        if (params.report_name !== undefined) {
            changes.name = String(params.report_name).trim();
        }
        if (params.report_description !== undefined) {
            changes.description = String(params.report_description);
        }
        if (params.report_type !== undefined) {
            changes.report_type = params.report_type;
        }
        if (params.report_orientation !== undefined) {
            changes.report_orientation = params.report_orientation;
        }
        if (params.data_source !== undefined) {
            changes.data_source = params.data_source;
        }
        if (params.include_benchmark_default !== undefined) {
            changes.include_benchmark_default = Boolean(params.include_benchmark_default);
        }
        (Object.keys(changes) as (keyof CustomPDFReportStore)[]).forEach((key) => {
            if (changes[key] === report[key]) {
                delete changes[key];
            }
        });
        return changes;
    }

    /**
     * Turns the tool's values-only layout into store modules. A module that names a module_id (saved) or step_uuid
     * (added in the builder, not saved yet) starts from the module already in the builder, so its settings and
     * section membership survive; any other starts from the menu variant for its report type, so field metadata
     * and the number of required securities match the report.
     */
    private _buildModules(
        pages: UpdateReportBuilderModuleParams[][],
        currentModules: CustomPDFReportModule[][],
        menu: CustomPDFReportModuleMenu,
        target: CustomPDFReportStore,
        warnings: string[]
    ): CustomPDFReportModule[][] {
        const menuModulesByVariant: Record<string, CustomPDFReportModule> = {};
        const defaultReportTypeByType: Record<string, CustomPDFReportType> = {};
        Object.values(menu).forEach((categoryModules) => {
            categoryModules.forEach((categoryModule) => {
                Object.entries(categoryModule.module_menu).forEach(([reportType, modulesBySize]) => {
                    Object.values(modulesBySize).forEach((menuModule) => {
                        menuModulesByVariant[`${menuModule.module_type}:${reportType}`] = menuModule;
                        defaultReportTypeByType[menuModule.module_type] = categoryModule.default_report_type;
                    });
                });
            });
        });
        const currentModulesByIdentity: Record<string, CustomPDFReportModule> = {};
        currentModules.flat().forEach((module) => {
            currentModulesByIdentity[getModuleIdentityKey(module)] = module;
        });

        const unknownTypes: string[] = [];
        const unknownReferences: string[] = [];
        const builtPages = pages.map((page) =>
            page.flatMap((params) => {
                const x = params.x_coordinate ?? 0;
                const y = params.y_coordinate ?? 0;
                const reference = params.module_id ?? params.step_uuid;
                let module: CustomPDFReportModule;
                if (reference != null) {
                    const current = currentModulesByIdentity[`${params.module_type}:${reference}`];
                    if (!current) {
                        unknownReferences.push(`${params.module_type} ${reference}`);
                        return [];
                    }
                    module = { ...cloneDeep(current), x_coordinate: x, y_coordinate: y };
                    if (params.report_type) {
                        module.report_type = params.report_type;
                    }
                } else {
                    const requestedType = params.report_type ?? target.report_type;
                    const menuModule =
                        menuModulesByVariant[`${params.module_type}:${requestedType}`] ??
                        menuModulesByVariant[`${params.module_type}:${defaultReportTypeByType[params.module_type]}`];
                    if (!menuModule) {
                        unknownTypes.push(params.module_type);
                        return [];
                    }
                    module = addModuleToReport(cloneDeep(menuModule), x, y);
                    if ('include_benchmark' in module.customizable_fields) {
                        module.customizable_fields.include_benchmark.value = target.include_benchmark_default;
                    }
                }
                Object.entries(params.customizable_fields ?? {}).forEach(([field, value]) => {
                    if (field in module.customizable_fields) {
                        module.customizable_fields[field].value = value;
                    } else {
                        warnings.push(`${params.module_type} has no field "${field}"; it was ignored.`);
                    }
                });
                return [module];
            })
        );

        if (unknownReferences.length) {
            throw new Error(
                `These modules are not in the Report Builder: ${[...new Set(unknownReferences)].join(', ')}. ` +
                    'Use the module_id or step_uuid values from get_current_page_context.'
            );
        }
        if (unknownTypes.length) {
            throw new Error(
                `These module types are not available for this report: ${[...new Set(unknownTypes)].join(', ')}. ` +
                    'Use fetch_pdf_report_module_definition to see the available modules.'
            );
        }
        return builtPages;
    }
}
